import { VersionedViewModelBase } from '@/components/basedata/VersionedViewModelBase'
import ImageVault from '@/data/ImageVaultData'
import { brandsMapper } from '@/store/modules/brands'
import { categoriesMapper } from '@/store/modules/categories'
import { centersMapper } from '@/store/modules/centers'
import { languagesMapper } from '@/store/modules/languages'
import { masterStoresMapper } from '@/store/modules/masterStores'
import { servicesMapper } from '@/store/modules/services'
import { subcategoriesMapper } from '@/store/modules/subcategories'
import { Category, CategoryLgVersion } from '@/types/categories'
import { Center, CenterLgVersion } from '@/types/centers'
import { Language } from '@/types/languages'
import { MasterStore, MasterStoreLgVersion } from '@/types/masterStores'
import { ServiceLgVersion } from '@/types/services'
import {
  Store,
  StoreCenterService,
  StoreContact,
  StoreLgVersion,
  StoreMasterStoreService,
  StoreOpenPeriod,
  StoreService,
  StoreSpecialOpenPeriod,
  UseOpeningHours
} from '@/types/stores'
import { Subcategory, SubcategoryLgVersion } from '@/types/subcategories'
import { localeSort } from '@/utils/formatting'
import { getLgVersion } from '@/utils/language'

export interface LookupItem {
  id: number
  name: string
}

export interface SubcategoryLookupItem {
  id: number
  categoryId: number
  categoryName: string
  name: string
  fullName: string
}

export interface MasterStoreLookupItem {
  id: number
  name: string
  languages: Language[]
}

export default VersionedViewModelBase<Store, StoreLgVersion>().extend({
  name: 'StoreViewModel',

  data: () => {
    return {
      m_masterStore: null as MasterStore | null,
      m_center: null as Center | null
    }
  },

  computed: {
    ...masterStoresMapper.mapState({
      allMasterStores: 'masterStores'
    }),
    ...centersMapper.mapState({
      allCenters: 'centers'
    }),
    ...categoriesMapper.mapState({
      allCategories: 'categories'
    }),
    ...subcategoriesMapper.mapState({
      allSubcategories: 'subcategories'
    }),
    ...brandsMapper.mapState({
      allBrands: 'brands'
    }),
    ...servicesMapper.mapState({
      allServices: 'services'
    }),
    ...languagesMapper.mapState({
      allLanguages: 'languages'
    }),

    isNew (): boolean {
      return !this.model.storeId
    },

    // Model:
    storeId: {
      get (): number {
        return this.model.storeId
      },
      set (value: number) {
        this.model.storeId = value
      }
    },
    masterStoreId: {
      get (): number {
        return this.model.masterStoreId
      },
      set (value: number) {
        this.model.masterStoreId = value
        this.loadMasterStoreDetails({ masterStoreId: value })
          .then(() => {
            if (!this.lgModel.name && !!this.masterStoreLgVersion) {
              this.lgModel.name = this.masterStoreLgVersion.name
            }
          })
          .catch(error => {
            console.log('Kunne ikke laste masterbutikk: ' + error)
          })
      }
    },
    centerId: {
      get (): number {
        return this.model.centerId
      },
      set (value: number) {
        this.model.centerId = value
      }
    },
    googlePlaceId: {
      get (): string | null {
        return this.model.googlePlaceId
      },
      set (value: string | null) {
        this.model.googlePlaceId = value
      }
    },
    wayfindingOnly: {
      get (): boolean {
        return this.model.wayfindingOnly
      },
      set (value: boolean) {
        this.model.wayfindingOnly = value
      }
    },
    homePageUrl: {
      get (): string | null {
        return this.model.homePageUrl
      },
      set (value: string | null) {
        this.model.homePageUrl = value
      }
    },
    facebookUrl: {
      get (): string | null {
        return this.model.facebookUrl
      },
      set (value: string | null) {
        this.model.facebookUrl = value
      }
    },
    instagramUrl: {
      get (): string | null {
        return this.model.instagramUrl
      },
      set (value: string | null) {
        this.model.instagramUrl = value
      }
    },
    phone: {
      get (): string | null {
        return this.model.phone
      },
      set (value: string | null) {
        this.model.phone = value
      }
    },
    email: {
      get (): string | null {
        return this.model.email
      },
      set (value: string | null) {
        this.model.email = value
      }
    },
    feedbackUrl: {
      get (): string | null {
        return this.model.feedbackUrl
      },
      set (value: string | null) {
        this.model.feedbackUrl = value
      }
    },
    useOpeningHours: {
      get (): UseOpeningHours {
        return this.model.useOpeningHours
      },
      set (value: UseOpeningHours) {
        this.model.useOpeningHours = value
      }
    },
    published: {
      get (): boolean {
        return this.model.published
      },
      set (value: boolean) {
        this.model.published = value
      }
    },
    fenistraDb: {
      get (): string | null {
        return this.model.fenistraDb
      },
      set (value: string | null) {
        this.model.fenistraDb = value
      }
    },
    fenistraId: {
      get (): number | null {
        return this.model.fenistraId
      },
      set (value: number | null) {
        this.model.fenistraId = value
      }
    },
    fenistraLocationDb: {
      get (): string | null {
        return this.model.fenistraLocationDb
      },
      set (value: string | null) {
        this.model.fenistraLocationDb = value
      }
    },
    fenistraLocationId: {
      get (): number | null {
        return this.model.fenistraLocationId
      },
      set (value: number | null) {
        this.model.fenistraLocationId = value
      }
    },
    createdDate: {
      get (): Date | undefined {
        return this.model.created
      },
      set (value: Date | undefined) {
        this.model.created = value
      }
    },
    lastUpdatedDate: {
      get (): Date | undefined {
        return this.model.lastUpdated
      },
      set (value: Date | undefined) {
        this.model.lastUpdated = value
      }
    },

    // lgModel:
    name: {
      get (): string {
        return this.lgModel.name
      },
      set (value: string) {
        this.lgModel.name = value
      }
    },
    description: {
      get (): string | null {
        return this.lgModel.description
      },
      set (value: string | null) {
        this.lgModel.description = value
      }
    },
    logoImageVaultId: {
      get (): number | null {
        return this.lgModel.logoImageVaultId
      },
      set (value: number | null) {
        this.lgModel.logoImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.lgModel.logoImageVaultId,
          url => (this.lgModel.logoUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.logoImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    logoUrl: {
      get (): string | null {
        return this.lgModel.logoUrl
      },
      set (value: string | null) {
        this.lgModel.logoUrl = value
      }
    },
    wayfinderLogoImageVaultId: {
      get (): number | null {
        return this.lgModel.wayfinderLogoImageVaultId
      },
      set (value: number | null) {
        this.lgModel.wayfinderLogoImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.lgModel.wayfinderLogoImageVaultId,
          url => (this.lgModel.wayfinderLogoUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.wayfinderLogoImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    wayfinderLogoUrl: {
      get (): string | null {
        return this.lgModel.wayfinderLogoUrl
      },
      set (value: string | null) {
        this.lgModel.wayfinderLogoUrl = value
      }
    },
    mainImageVaultId: {
      get (): number | null {
        return this.lgModel.mainImageVaultId
      },
      set (value: number | null) {
        this.lgModel.mainImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.lgModel.mainImageVaultId,
          url => (this.lgModel.mainImageUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.mainImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    mainImageUrl: {
      get (): string | null {
        return this.lgModel.mainImageUrl
      },
      set (value: string | null) {
        this.lgModel.mainImageUrl = value
      }
    },
    collageImageVaultIds: {
      get (): string | null {
        return this.lgModel.collageImageVaultIds
      },
      set (value: string | null) {
        this.lgModel.collageImageVaultIds = value

        ImageVault.updateUrlsFromImageVaultIdsDebounced(
          this.lgModel.collageImageVaultIds,
          urls => (this.lgModel.collageImageUrls = urls),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.collageImageVaultIds,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    collageImageUrls: {
      get (): string | null {
        return this.lgModel.collageImageUrls
      },
      set (value: string | null) {
        this.lgModel.collageImageUrls = value
      }
    },
    floor: {
      get (): string | null {
        return this.lgModel.floor
      },
      set (value: string | null) {
        this.lgModel.floor = value
      }
    },
    parking: {
      get (): string | null {
        return this.lgModel.parking
      },
      set (value: string | null) {
        this.lgModel.parking = value
      }
    },
    temporarilyClosedMessage: {
      get (): string | null {
        return this.lgModel.temporarilyClosedMessage
      },
      set (value: string | null) {
        this.lgModel.temporarilyClosedMessage = value
      }
    },

    // Existing language versions:
    languageVersions (): string[] {
      return this.model.lgVersions.map(x => x.lgCode)
    },

    // Related:
    masterStore (): MasterStore | undefined {
      return this.allMasterStores.find(
        x => x.masterStoreId == this.model.masterStoreId
      ) as MasterStore
    },
    masterStoreUiVersion (): MasterStoreLgVersion | null {
      return this.masterStore
        ? getLgVersion<MasterStoreLgVersion>(
          this.masterStore,
          this.lgModel ? this.lgModel.lgCode : this.$uiLanguage
        )
        : null
    },
    masterStoreLgVersion (): MasterStoreLgVersion | null {
      return this.masterStore
        ? getLgVersion<MasterStoreLgVersion>(
          this.masterStore,
          this.lgModel ? this.lgModel.lgCode : this.$uiLanguage
        )
        : null
    },
    masterStoreName (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.name : null
    },
    center (): Center | undefined {
      return this.allCenters.find(x => x.centerId == this.model.centerId)
    },
    centerLgVersion (): CenterLgVersion | null {
      return this.center
        ? getLgVersion<CenterLgVersion>(
          this.center,
          this.lgModel ? this.lgModel.lgCode : this.$uiLanguage
        )
        : null
    },
    centerName (): string | null {
      return this.centerLgVersion ? this.centerLgVersion.name : null
    },

    // Inherited values:
    inheritedDescription (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.description : null
    },
    inheritedDescriptionBody (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.descriptionBody : null
    },
    inheritedMetaDescription (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.metaDescription : null
    },
    inheritedHomePageUrl (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.homePageUrl : null
    },
    inheritedFacebookUrl (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.facebookUrl : null
    },
    inheritedInstagramUrl (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.instagramUrl : null
    },
    inheritedLogoImageVaultId (): number | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.logoImageVaultId : null
    },
    inheritedLogoUrl (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.logoUrl : null
    },
    inheritedWayfinderLogoImageVaultId (): number | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.wayfinderLogoImageVaultId : null
    },
    inheritedWayfinderLogoUrl (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.wayfinderLogoUrl : null
    },
    inheritedMainImageVaultId (): number | null {
      return !!this.masterStoreUiVersion && !!this.masterStoreUiVersion.mainImageVaultId
        ? this.masterStoreUiVersion.mainImageVaultId
        : this.defaultMainImageVaultId
    },
    defaultMainImageVaultId (): number | null {
      const subcategories = this.inheritedSubcategories.concat(this.model.subcategories)
      const subcategory = this.mapToSubcategories(subcategories).find(
        x => x.defaultStoreMainImageVaultId
      )
      if (subcategory) return subcategory.defaultStoreMainImageVaultId
      return null
    },
    inheritedMainImageUrl (): string | null {
      return !!this.masterStoreUiVersion && !!this.masterStoreUiVersion.mainImageUrl
        ? this.masterStoreUiVersion.mainImageUrl
        : this.defaultMainImageUrl
    },
    defaultMainImageUrl (): string | null {
      const subcategories = this.inheritedSubcategories.concat(this.model.subcategories)
      const subcategory = this.mapToSubcategories(subcategories).find(x => x.defaultStoreMainImageUrl)
      if (subcategory) return subcategory.defaultStoreMainImageUrl
      return null
    },
    inheritedCollageImageVaultIds (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.collageImageVaultIds : null
    },
    inheritedCollageImageUrls (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.collageImageUrls : null
    },
    inheritedTemporarilyClosedMessage (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.temporarilyClosedMessage : null
    },
    inheritedPhone (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.phone : null
    },
    inheritedEmail (): string | null {
      return this.masterStoreUiVersion ? this.masterStoreUiVersion.email : null
    },

    // Inherited collections:
    inheritedCategories (): number[] {
      return this.masterStore ? this.masterStore.categories : []
    },
    inheritedSubcategories (): number[] {
      return this.masterStore ? this.masterStore.subcategories : []
    },

    // Effective values:
    effectiveHomePageUrl (): string | null {
      return this.model.homePageUrl ? this.model.homePageUrl : this.inheritedHomePageUrl
    },
    effectiveFacebookUrl (): string | null {
      return this.model.facebookUrl ? this.model.facebookUrl : this.inheritedFacebookUrl
    },
    effectiveInstagramUrl (): string | null {
      return this.model.instagramUrl ? this.model.instagramUrl : this.inheritedInstagramUrl
    },
    effectiveLogoImageVaultId (): number | null {
      return this.lgModel.logoImageVaultId
        ? this.lgModel.logoImageVaultId
        : this.inheritedLogoImageVaultId
    },
    effectiveLogoUrl (): string | null {
      return this.lgModel.logoUrl ? this.lgModel.logoUrl : this.inheritedLogoUrl
    },
    effectiveWayfinderLogoImageVaultId (): number | null {
      return this.lgModel.wayfinderLogoImageVaultId
        ? this.lgModel.wayfinderLogoImageVaultId
        : this.inheritedWayfinderLogoImageVaultId
    },
    effectiveWayfinderLogoUrl (): string | null {
      return this.lgModel.wayfinderLogoUrl ? this.lgModel.wayfinderLogoUrl : this.inheritedWayfinderLogoUrl
    },
    effectiveMainImageVaultId (): number | null {
      return this.lgModel.mainImageVaultId
        ? this.lgModel.mainImageVaultId
        : this.inheritedMainImageVaultId
    },
    effectiveMainImageUrl (): string | null {
      return this.lgModel.mainImageUrl ? this.lgModel.mainImageUrl : this.inheritedMainImageUrl
    },
    effectiveCollageImageVaultIds (): string | null {
      return this.lgModel.collageImageVaultIds
        ? this.lgModel.collageImageVaultIds
        : this.inheritedCollageImageVaultIds
    },
    effectiveCollageImageUrls (): string | null {
      return this.lgModel.collageImageUrls
        ? this.lgModel.collageImageUrls
        : this.inheritedCollageImageUrls
    },
    effectiveTemporarilyClosedMessage (): string | null {
      return this.lgModel.temporarilyClosedMessage
        ? this.lgModel.temporarilyClosedMessage
        : this.inheritedTemporarilyClosedMessage
    },
    effectivePhone (): string | null {
      return this.model.phone ? this.model.phone : this.inheritedPhone
    },
    effectiveEmail (): string | null {
      return this.model.email ? this.model.email : this.inheritedEmail
    },

    // Collections:
    categories: {
      get (): number[] {
        return this.model.categories
      },
      set (value: number[]) {
        this.model.categories = value
      }
    },
    subcategories: {
      get (): number[] {
        return this.model.subcategories
      },
      set (value: number[]) {
        this.model.subcategories = value
      }
    },
    brands: {
      get (): number[] {
        return this.model.brands
      },
      set (value: number[]) {
        this.model.brands = value
      }
    },
    storeServices: {
      get (): StoreService[] {
        return this.model.storeServices
      },
      set (value: StoreService[]) {
        this.model.storeServices = value
      }
    },
    centerServices: {
      get (): StoreCenterService[] {
        return this.model.centerServices
      },
      set (value: StoreCenterService[]) {
        this.model.centerServices = value
      }
    },
    masterStoreServices: {
      get (): StoreMasterStoreService[] {
        return this.model.masterStoreServices
      },
      set (value: StoreMasterStoreService[]) {
        this.model.masterStoreServices = value
      }
    },
    contacts: {
      get (): StoreContact[] {
        return this.model.contacts
      },
      set (value: StoreContact[]) {
        this.model.contacts = value
      }
    },
    openPeriods: {
      get (): StoreOpenPeriod[] {
        return this.model.openPeriods
      },
      set (value: StoreOpenPeriod[]) {
        this.model.openPeriods = value
      }
    },
    specialOpenPeriods: {
      get (): StoreSpecialOpenPeriod[] {
        return this.model.specialOpenPeriods
      },
      set (value: StoreSpecialOpenPeriod[]) {
        this.model.specialOpenPeriods = value
      }
    },

    // Lookups:
    masterStoreLookups (): MasterStoreLookupItem[] {
      return this.allMasterStores
        .map(x => ({
          id: x.masterStoreId,
          name: getLgVersion<MasterStoreLgVersion>(x, this.$uiLanguage).name,
          languages: x.lgVersions.map(x => this.allLanguages.find(y => y.lgCode == x.lgCode)!)
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    },
    centerLookups (): LookupItem[] {
      return this.allCenters
        .map(x => ({
          id: x.centerId,
          name: getLgVersion<CenterLgVersion>(x, this.$uiLanguage).name
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    },
    categoryLookups (): LookupItem[] {
      return this.allCategories
        .map(x => ({
          id: x.categoryId,
          name: getLgVersion<CategoryLgVersion>(x, this.$uiLanguage).name
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    },
    subcategoryLookups (): SubcategoryLookupItem[] {
      return this.allSubcategories
        .map(x => ({
          subcategory: x,
          category: this.allCategories.find(y => y.categoryId == x.categoryId)!
        }))
        .filter(x => x.category)
        .map(x => {
          const name = getLgVersion<SubcategoryLgVersion>(x.subcategory, this.$uiLanguage).name
          const categoryName = getLgVersion<CategoryLgVersion>(x.category, this.$uiLanguage).name
          return {
            id: x.subcategory.subcategoryId,
            categoryId: x.category.categoryId,
            name: name,
            categoryName: categoryName,
            fullName: categoryName + ' - ' + name
          }
        })
        .sort((a, b) => localeSort(a.fullName, b.fullName))
    },
    brandLookups (): LookupItem[] {
      return this.allBrands
        .map(x => ({ id: x.brandId, name: x.name }))
        .sort((a, b) => localeSort(a.name, b.name))
    },
    serviceLookups (): LookupItem[] {
      return this.allServices
        .map(x => ({
          id: x.serviceId,
          name: getLgVersion<ServiceLgVersion>(x, this.$uiLanguage).name
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    }
  },

  methods: {
    ...masterStoresMapper.mapActions({
      loadMasterStores: 'loadMasterStores',
      loadMasterStoreDetails: 'loadMasterStoreDetails'
    }),
    ...centersMapper.mapActions({
      loadCenters: 'loadCenters',
      loadCenterDetails: 'loadCenterDetails'
    }),
    ...categoriesMapper.mapActions({
      loadCategories: 'loadCategories'
    }),
    ...subcategoriesMapper.mapActions({
      loadSubcategories: 'loadSubcategories'
    }),
    ...brandsMapper.mapActions({
      loadBrands: 'loadBrands'
    }),
    ...servicesMapper.mapActions({
      loadServices: 'loadServices'
    }),
    ...languagesMapper.mapActions({
      loadLanguages: 'loadLanguages'
    }),

    // Mappings:
    mapToCategories (categories: number[]): Category[] {
      return categories.map(categoryId => this.allCategories.find(x => x.categoryId == categoryId)!)
    },
    mapToCategoryLookups (categories: number[]): LookupItem[] {
      return categories.map(x => this.categoryLookups.find(y => y.id == x)!)
    },
    mapToSubcategories (subcategories: number[]): Subcategory[] {
      return subcategories.map(x => this.allSubcategories.find(y => y.subcategoryId == x)!)
    },
    mapToSubcategoryLookups (subcategories: number[]): SubcategoryLookupItem[] {
      return subcategories.map(x => this.subcategoryLookups.find(y => y.id == x)!)
    }
  },

  watch: {
    'model.masterStoreId': {
      handler (masterStoreId: number): void {
        if (this.model.masterStoreId) {
          this.loadMasterStoreDetails({ masterStoreId: masterStoreId })
            .then(() => {
              if (!this.lgModel.name && !!this.masterStoreLgVersion) {
                this.lgModel.name = this.masterStoreLgVersion.name
              }
            })
            .catch(error => {
              console.log('Kunne ikke laste masterbutikk: ' + error)
            })
        } else {
          this.m_masterStore = null
        }
      },
      immediate: true
    },
    'model.centerId': {
      handler (centerId: number): void {
        if (this.model.centerId) {
          this.loadCenterDetails({ centerId: centerId }).catch(error => {
            console.log('Kunne ikke laste senter: ' + error)
          })
        } else {
          this.m_center = null
        }
      },
      immediate: true
    },
    'model.categories': {
      handler (value: number[]) {
        // Do nothing
        // For some deep and mystical reason, if this watcher is not present, Vue reactivity (?) will create a watcher (during rendering?)
        // with an empty callback, which generates an error message:
        //    vue.runtime.esm.js:619 [Vue warn]: Error in callback for watcher "model.categories": "TypeError: Cannot read properties of undefined (reading 'call')"
        // The error originates at vue.runtime.esm.js, in the code for Watcher.prototype.run:
      }
    }
  },

  mounted (): void {
    this.loadCenters({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste kjøpesentre: ' + error)
      if (this.allCenters.length > 0) console.log('Viser bufrede data.')
    })
    this.loadMasterStores({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste masterbutikker: ' + error)
      if (this.allMasterStores.length > 0) console.log('Viser bufrede data.')
    })
    this.loadCategories({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste kategorier: ' + error)
      if (this.allCategories.length > 0) console.log('Viser bufrede data.')
    })
    this.loadSubcategories({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste underkategorier: ' + error)
      if (this.allSubcategories.length > 0) console.log('Viser bufrede data.')
    })
    this.loadBrands({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste merkevarer: ' + error)
      if (this.allBrands.length > 0) console.log('Viser bufrede data.')
    })
    this.loadServices({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste tjenester: ' + error)
      if (this.allServices.length > 0) console.log('Viser bufrede data.')
    })
    this.loadLanguages({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste språk: ' + error)
      if (this.allLanguages.length > 0) console.log('Viser bufrede data.')
    })
  }
})
